import { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { MapPin, Search, ChevronRight, X, List, CalendarDays, Clock, LayoutGrid, ArrowUpRight, Flame, Users, Sparkles } from 'lucide-react';
import { format, parseISO, differenceInCalendarDays } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useLangPrefix, withLangPrefix } from '../hooks/useLangPrefix';
import { QualiopiBadge } from '../components/QualiopiBadge';

const SA = "/sa";

const FORMATION_IMAGES = {
  decouverte: `${SA}/fabrication-dun-coussin.jpg`,
  complete:   `${SA}/nico-machine-a-coudre-scaled-1.jpg`,
  auto:       `${SA}/IMG_20220428_194010238-scaled-1.jpg`,
  moto:       `${SA}/IMG_20220401_105744457-scaled-1.jpg`,
  nautique:   `${SA}/img014.jpg`,
  habitat:    `${SA}/38a18be2087b9b5e14b06781c43f0250.jpg`,
  dessin:     `${SA}/IMG_20220330_182110015-scaled-1.jpg`,
};

const FORMATION_PROMISES = {
  decouverte: 'Valide ton intérêt pour le métier en 3 jours',
  complete:   'Deviens sellier polyvalent en 23 jours',
  auto:       'Restaure des intérieurs auto haut de gamme',
  moto:       'Crée des selles sur-mesure professionnelles',
  nautique:   'Maîtrise la sellerie de bateaux',
  habitat:    'Réalise fauteuils, banquettes & sellerie médicale',
  dessin:     'Trace tes propres patrons et gabarits',
  cover:      'Donnez une nouvelle vie, avec un nouveau style !',
};

const categoryLabels = {
  all: 'Toutes',
  complete: 'Entrepreneur',
  cover: 'Recovering',
  base: 'Base',
};

const categoryLong = {
  decouverte: 'Découverte',
  complete: 'Formation complète',
  auto: 'Sellerie automobile',
  moto: 'Sellerie moto',
  nautique: 'Sellerie nautique',
  habitat: 'Habitat & médical',
  dessin: 'Dessin technique',
  cover: 'Cover Multisupport®',
};

// ─────────────────────────────────────────────
// AGENDA VIEW — chronological sessions grouped by month
// ─────────────────────────────────────────────
const AgendaView = ({ sessionsByMonth, courses }) => {
  const { lang } = useLangPrefix();
  if (sessionsByMonth.length === 0) {
    return (
      <div className="py-24 text-center" data-testid="agenda-empty">
        <h3 className="font-light text-2xl md:text-4xl text-[#1F2A4A] mb-3">
          Pas de session planifiée <span className="font-serif italic">pour cette sélection.</span>
        </h3>
        <p className="text-[#4A5568] mb-8">Essaie une autre catégorie ou contacte-nous.</p>
      </div>
    );
  }

  return (
    <div className="space-y-16 md:space-y-20" data-testid="agenda-view">
      {sessionsByMonth.map(([monthKey, group]) => (
        <div key={monthKey}>
          {/* Month header with calendar icon */}
          <div className="flex items-baseline gap-5 mb-8">
            <span className="h-px flex-1 bg-[#1F2A4A]/12" />
            <span className="text-xs uppercase tracking-[0.25em] text-[#4A5568]">
              {group.items.length} session{group.items.length > 1 ? 's' : ''}
            </span>
          </div>

          <h3 className="font-light text-3xl md:text-4xl lg:text-5xl text-[#1F2A4A] mb-8 capitalize">
            {group.label}
          </h3>

          {/* Sessions list */}
          <ul className="border-t border-[#1F2A4A]/12">
            {group.items.map((s, idx) => {
              const course = courses.find(c => c.id === s.course_id);
              if (!course) return null;
              const start = parseISO(s.start_date);
              const end = parseISO(s.end_date);
              // Use the course's pedagogical duration (e.g. 23 jours lundi→vendredi)
              // rather than the raw calendar span between start/end dates.
              const days = course.duration_days || (differenceInCalendarDays(end, start) + 1);
              const left = s.available_spots - s.enrolled_count;
              const ratio = s.available_spots > 0 ? Math.max(0, Math.min(100, (s.enrolled_count / s.available_spots) * 100)) : 0;
              const fillingFast = left > 0 && left <= 3;

              return (
                <li
                  key={s.id}
                  className="border-b border-dashed border-[#1F2A4A]/15 hover:bg-[#F0EBE0] transition-colors duration-500 group"
                  data-testid={`agenda-session-${s.id}`}
                >
                  <Link to={withLangPrefix(`/candidature/${s.id}`, lang)} className="grid grid-cols-12 items-stretch gap-3 md:gap-6 py-6 md:py-7 px-2 md:px-4">
                    {/* DATE CARD */}
                    <div className="col-span-3 md:col-span-2">
                      <div className="inline-flex flex-col items-center justify-center w-16 h-20 md:w-20 md:h-24 rounded-xl bg-white border border-[#1F2A4A]/10 group-hover:border-[#C9B896] transition-colors">
                        <span className="text-[10px] uppercase tracking-wider text-[#C9B896] font-semibold">
                          {format(start, 'MMM', { locale: fr })}
                        </span>
                        <span className="text-3xl md:text-4xl font-light text-[#1F2A4A] leading-none mt-1">
                          {format(start, 'd')}
                        </span>
                        <span className="text-[10px] uppercase tracking-wider text-[#4A5568] mt-1">
                          {format(start, 'EEE', { locale: fr })}
                        </span>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="col-span-9 md:col-span-6 flex flex-col justify-center">
                      <p className="text-[10px] uppercase tracking-[0.25em] text-[#4A5568] mb-1.5">
                        {categoryLong[course.category] || course.category}
                      </p>
                      <h4 className="text-lg md:text-2xl font-light text-[#1F2A4A] group-hover:text-[#C9B896] transition-colors leading-tight">
                        {course.title}
                      </h4>
                      <div className="flex flex-wrap items-center gap-4 mt-3 text-xs text-[#4A5568] font-light">
                        <span className="inline-flex items-center gap-1.5">
                          <Clock className="w-3 h-3 text-[#C9B896]" />
                          {days} jour{days > 1 ? 's' : ''}
                        </span>
                        <span className="text-[#1F2A4A]/30">·</span>
                        <span>Du {format(start, 'd MMM', { locale: fr })} au {format(end, 'd MMM yyyy', { locale: fr })}</span>
                        <span className="text-[#1F2A4A]/30">·</span>
                        <span className="inline-flex items-center gap-1.5">
                          <MapPin className="w-3 h-3 text-[#C9B896]" />
                          {s.location || 'Istres'}
                        </span>
                      </div>
                    </div>

                    {/* Spots + price + cta */}
                    <div className="col-span-12 md:col-span-4 flex flex-col md:items-end justify-center gap-2 mt-2 md:mt-0">
                      {/* Spots progress — capacity is 3 max, so every session is urgent */}
                      <div className="w-full md:w-44">
                        <div className="flex items-center justify-between mb-1.5">
                          <span className={`text-xs font-semibold inline-flex items-center gap-1 ${
                            left === 0 ? 'text-red-700' : left === 1 ? 'text-red-600' : 'text-amber-700'
                          }`}>
                            {left === 0 ? (
                              <>Complet</>
                            ) : (
                              <>
                                <Flame className={`w-3 h-3 ${left === 1 ? 'text-red-600' : 'text-amber-600'}`} />
                                {left === 1 ? `Dernière place — vite !` : `${left} places restantes`}
                              </>
                            )}
                          </span>
                          <span className="text-[10px] text-[#4A5568]/55">{s.enrolled_count}/{s.available_spots}</span>
                        </div>
                        <div className="h-1 w-full bg-[#1F2A4A]/8 rounded-full overflow-hidden">
                          <div
                            className={`h-full transition-all duration-700 ${
                              left === 0 ? 'bg-red-500' : left === 1 ? 'bg-red-500' : 'bg-amber-500'
                            }`}
                            style={{ width: `${ratio}%` }}
                          />
                        </div>
                      </div>
                      <div className="flex items-baseline gap-4 md:gap-5 mt-2">
                        <span className="font-bold text-2xl md:text-[26px] text-[#1F2A4A]">
                          {course.price.toFixed(0)}<span className="text-base text-[#C9B896] ml-1 font-semibold">€</span>
                        </span>
                        {left > 0 ? (
                          <span className="text-[#C9B896] text-xl group-hover:translate-x-2 transition-transform duration-500">→</span>
                        ) : (
                          <span className="text-[#4A5568]/40 text-xs italic">complet</span>
                        )}
                      </div>
                    </div>
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </div>
  );
};

export const OnsiteCoursesPage = () => {
  const { lang } = useLangPrefix();
  const [courses, setCourses] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState('');
  const [view, setView] = useState(() => searchParams.get('view') || 'grid');

  const category = searchParams.get('category') || 'all';

  useEffect(() => {
    loadData();
  }, [category]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [coursesData, sessionsData] = await Promise.all([
        api.getCourses({ course_type: 'onsite', ...(category && category !== 'all' && { category }) }),
        api.getSessions()
      ]);
      setCourses(coursesData);
      setSessions(sessionsData);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const getNextSession = (courseId) => {
    const now = new Date();
    return sessions
      .filter(s => s.course_id === courseId && parseISO(s.start_date) > now)
      .sort((a, b) => parseISO(a.start_date) - parseISO(b.start_date))[0];
  };

  const filteredCourses = courses.filter(c =>
    c.category !== 'module' && (
      c.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.description.toLowerCase().includes(searchTerm.toLowerCase())
    )
  );
  const moduleCourses = courses.filter(c => c.category === 'module')
    .sort((a, b) => (a.module_parent || '').localeCompare(b.module_parent || '') || (a.module_order ?? 0) - (b.module_order ?? 0));

  // Group future sessions by month for the agenda view
  const sessionsByMonth = useMemo(() => {
    const now = new Date();
    const future = sessions
      .filter(s => parseISO(s.start_date) > now)
      .filter(s => {
        if (category === 'all') return true;
        const c = courses.find(co => co.id === s.course_id);
        return c && c.category === category;
      })
      .sort((a, b) => parseISO(a.start_date) - parseISO(b.start_date));

    const groups = {};
    future.forEach(s => {
      const monthKey = format(parseISO(s.start_date), 'yyyy-MM');
      const monthLabel = format(parseISO(s.start_date), 'MMMM yyyy', { locale: fr });
      if (!groups[monthKey]) groups[monthKey] = { label: monthLabel, items: [] };
      groups[monthKey].items.push(s);
    });
    return Object.entries(groups);
  }, [sessions, courses, category]);

  const handleViewChange = (v) => {
    setView(v);
    const params = new URLSearchParams(searchParams);
    if (v !== 'grid') params.set('view', v);
    else params.delete('view');
    setSearchParams(params);
  };

  return (
    <div className="bg-[#FAF7F0] text-[#1F2A4A] min-h-screen">
      {/* Hero — compact pour laisser apparaître les formations dès l'arrivée */}
      <section className="pt-28 pb-6 md:pt-32 md:pb-8">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center gap-2 text-xs text-[#4A5568] mb-4">
            <Link to={withLangPrefix('/', lang)} className="hover:text-[#C9B896] transition-colors">Accueil</Link>
            <ChevronRight className="w-3 h-3 text-[#C9B896]" />
            <span className="text-[#1F2A4A]">Formations sur place</span>
          </div>

          {/* Urgency banner — capacity is intentionally limited to 3 per session */}
          <div
            className="mb-5 inline-flex items-start gap-3 px-4 py-2.5 rounded-2xl bg-amber-50 border border-amber-300 ring-1 ring-amber-200"
            data-testid="onsite-seats-banner"
          >
            <Flame className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
            <div className="text-xs text-amber-900 leading-snug">
              <strong className="font-semibold">Seulement 3 places par session</strong> — pour préserver la qualité d'accompagnement.
              <span className="hidden sm:inline"> Le nombre de places restantes s'affiche en temps réel sur chaque ligne ci-dessous.</span>
              <span className="ml-1 text-amber-700">Vite, réserve la tienne&nbsp;!</span>
            </div>
          </div>

          <div className="grid grid-cols-12 gap-6 items-end">
            <div className="col-span-12 md:col-span-8">
              <h1 className="font-light text-[#1F2A4A] leading-[1.05] tracking-tight" data-testid="onsite-courses-title">
                <span className="text-3xl md:text-5xl">3 parcours, 21 modules,</span>{' '}
                <span className="font-serif italic text-3xl md:text-5xl text-[#C9B896]">un même atelier.</span>
              </h1>
              <p className="text-[#4A5568] text-sm md:text-base font-light max-w-2xl mt-3 leading-relaxed">
                En présentiel à Istres · De 2 à 23 jours · Plusieurs solutions de financement disponibles.
              </p>
            </div>
            <div className="hidden md:block col-span-4 md:text-right">
              <div className="inline-flex items-center gap-3 px-4 py-2.5 rounded-full bg-white border border-[#1F2A4A]/10">
                <MapPin className="w-3.5 h-3.5 text-[#C9B896]" strokeWidth={1.5} />
                <div className="text-left">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[#4A5568]">Atelier</p>
                  <p className="text-xs font-medium text-[#1F2A4A]">Istres · Provence</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="pt-2 pb-6 md:pt-3 md:pb-8">
        <div className="max-w-7xl mx-auto px-6 md:px-12 space-y-4">
          <div className="relative max-w-md">
            <Search className="absolute left-0 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#C9B896]" strokeWidth={1.5} />
            <Input
              placeholder="Rechercher une formation..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="bg-transparent border-0 border-b border-[#1F2A4A]/20 rounded-none pl-7 pr-8 focus:border-[#C9B896] focus-visible:ring-0 text-[#1F2A4A] font-light text-sm h-10"
              data-testid="search-onsite-courses"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-0 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full hover:bg-[#1F2A4A]/5 flex items-center justify-center"
                aria-label="Effacer"
              >
                <X className="w-3.5 h-3.5 text-[#1F2A4A]/60" />
              </button>
            )}
          </div>

          <div className="flex items-center justify-between gap-6 flex-wrap">
            <div className="flex flex-wrap items-center gap-x-7 gap-y-2 text-sm">
              {Object.entries(categoryLabels).map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => {
                    const params = new URLSearchParams();
                    if (key !== 'all') params.set('category', key);
                    if (view === 'agenda') params.set('view', 'agenda');
                    setSearchParams(params);
                  }}
                  data-testid={`filter-${key}`}
                  className={`pb-1 border-b transition-all duration-300 ${
                    category === key
                      ? 'text-[#1F2A4A] font-medium border-[#C9B896]'
                      : 'text-[#4A5568]/70 hover:text-[#1F2A4A] border-transparent hover:border-[#C9B896]/40'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* View toggle */}
            <div className="inline-flex items-center bg-white border border-[#1F2A4A]/10 rounded-full p-1" data-testid="view-toggle">
              <button
                onClick={() => handleViewChange('grid')}
                className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs uppercase tracking-[0.15em] font-medium transition-all duration-300 ${
                  view === 'grid' ? 'bg-[#1F2A4A] text-white' : 'text-[#4A5568] hover:text-[#1F2A4A]'
                }`}
                data-testid="view-grid"
              >
                <LayoutGrid className="w-3.5 h-3.5" /> Grille
              </button>
              <button
                onClick={() => handleViewChange('list')}
                className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs uppercase tracking-[0.15em] font-medium transition-all duration-300 ${
                  view === 'list' ? 'bg-[#1F2A4A] text-white' : 'text-[#4A5568] hover:text-[#1F2A4A]'
                }`}
                data-testid="view-list"
              >
                <List className="w-3.5 h-3.5" /> Liste
              </button>
              <button
                onClick={() => handleViewChange('agenda')}
                className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs uppercase tracking-[0.15em] font-medium transition-all duration-300 ${
                  view === 'agenda' ? 'bg-[#1F2A4A] text-white' : 'text-[#4A5568] hover:text-[#1F2A4A]'
                }`}
                data-testid="view-agenda"
              >
                <CalendarDays className="w-3.5 h-3.5" /> Agenda
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Boutique list */}
      <section className="pb-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {loading ? (
            <ul className="border-t border-[#1F2A4A]/12">
              {[...Array(5)].map((_, i) => (
                <li key={i} className="border-b border-dashed border-[#1F2A4A]/15 py-8 animate-pulse">
                  <div className="grid grid-cols-12 gap-4 items-center px-2 md:px-4">
                    <div className="col-span-1 h-3 bg-[#1F2A4A]/8 rounded" />
                    <div className="col-span-5 space-y-2">
                      <div className="h-2 bg-[#1F2A4A]/8 rounded w-1/3" />
                      <div className="h-5 bg-[#1F2A4A]/8 rounded w-3/4" />
                    </div>
                    <div className="col-span-2 h-4 bg-[#1F2A4A]/8 rounded" />
                    <div className="col-span-2 h-4 bg-[#1F2A4A]/8 rounded" />
                    <div className="col-span-2 h-6 bg-[#1F2A4A]/8 rounded" />
                  </div>
                </li>
              ))}
            </ul>
          ) : filteredCourses.length === 0 ? (
            <div className="py-24 text-center">
              <h3 className="font-light text-3xl md:text-4xl text-[#1F2A4A] mb-3">
                Aucune formation <span className="font-serif italic">ne correspond.</span>
              </h3>
              <p className="text-[#4A5568] mb-8">Essayez de modifier vos filtres.</p>
              <button
                onClick={() => { setSearchTerm(''); setSearchParams({}); }}
                className="group inline-flex items-baseline gap-3 text-[#1F2A4A] border-b border-[#C9B896] pb-1 hover:gap-5 transition-all duration-500"
              >
                <span className="text-sm uppercase tracking-[0.25em]">Réinitialiser</span>
                <span className="text-[#C9B896]">→</span>
              </button>
            </div>
          ) : view === 'agenda' ? (
            <AgendaView sessionsByMonth={sessionsByMonth} courses={courses} />
          ) : view === 'grid' ? (
            /* Editorial card grid — dense visual catalog, 3 columns on desktop */
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6" data-testid="onsite-formations-grid">
              {filteredCourses.map((course, idx) => {
                const next = getNextSession(course.id);
                const spots = next ? next.available_spots - next.enrolled_count : 0;
                const ratio = next && next.available_spots > 0
                  ? Math.max(0, Math.min(100, (next.enrolled_count / next.available_spots) * 100))
                  : 0;
                const fillingFast = spots > 0 && spots <= 3;
                const img = course.thumbnail_url || FORMATION_IMAGES[course.category] || FORMATION_IMAGES.complete;
                const promise = FORMATION_PROMISES[course.category];

                return (
                  <Link
                    key={course.id}
                    to={withLangPrefix(`/formation/${course.id}`, lang)}
                    className="group relative bg-white border border-[#1F2A4A]/10 rounded-2xl overflow-hidden hover:border-[#C9B896]/60 hover:shadow-[0_18px_44px_-22px_rgba(31,42,74,0.35)] transition-all duration-500"
                    data-testid={`onsite-course-card-${course.id}`}
                  >
                    {/* Image */}
                    <div className="relative aspect-[4/3] overflow-hidden bg-[#1F2A4A]">
                      <img
                        src={img}
                        alt={course.title}
                        loading="lazy"
                        className="w-full h-full object-cover saturate-[0.6] brightness-[0.7] contrast-[0.95] transition-all duration-[1200ms] group-hover:scale-[1.06] group-hover:saturate-[0.85] group-hover:brightness-[0.85]"
                      />
                      {/* Navy color wash + stronger bottom gradient to push title forward */}
                      <div className="absolute inset-0 bg-[#1F2A4A]/35 mix-blend-multiply pointer-events-none" />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0E1426] via-[#0E1426]/55 to-transparent" />

                      {/* Top-left: index + category */}
                      <div className="absolute top-3 left-3 flex items-center gap-2">
                        <span className="font-serif italic text-[#C9B896] text-2xl leading-none drop-shadow-md">
                          {String(idx + 1).padStart(2, '0')}
                        </span>
                        <span className="text-[9px] font-mono uppercase tracking-[0.25em] text-white/90 bg-[#1F2A4A]/55 backdrop-blur-sm px-2 py-1 rounded-full border border-white/15">
                          {categoryLong[course.category] || course.category}
                        </span>
                      </div>

                      {/* Top-right: price chip */}
                      <div className="absolute top-3 right-3 bg-white/95 backdrop-blur-sm rounded-full px-3.5 py-1.5 text-[#1F2A4A] shadow-md">
                        <span className="font-bold text-base">{course.price.toFixed(0)}</span>
                        <span className="text-[#C9B896] text-sm ml-0.5 font-semibold">€</span>
                      </div>

                      {/* NOUVEAUTÉ badge — highlights the freshest offer(s) */}
                      {course.category === 'cover' && (
                        <div
                          className="absolute top-14 right-3 inline-flex items-center gap-1 bg-gradient-to-r from-[#C9B896] to-[#E0D4BA] text-[#1F2A4A] text-[10px] uppercase tracking-[0.22em] font-bold px-2.5 py-1 rounded-full shadow-lg border border-white/40"
                          data-testid={`badge-nouveaute-${course.id}`}
                        >
                          <Sparkles className="w-3 h-3" strokeWidth={2.4} />
                          Nouveauté
                        </div>
                      )}

                      {/* Filling fast / complet ribbon — always urgent (3 max) */}
                      {next && spots > 0 && (
                        <div className={`absolute bottom-20 left-3 inline-flex items-center gap-1 ${
                          spots === 1 ? 'bg-red-600/95' : 'bg-amber-500/95'
                        } text-white text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded-full shadow-sm`}>
                          <Flame className="w-3 h-3" /> {spots === 1 ? 'Dernière place' : `Plus que ${spots} places`}
                        </div>
                      )}
                      {next && spots === 0 && (
                        <div className="absolute bottom-20 left-3 bg-red-600/95 text-white text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded-full shadow-sm">
                          Complet
                        </div>
                      )}

                      {/* Bottom: title overlay — readable sans-serif, strong presence */}
                      <div className="absolute bottom-0 left-0 right-0 px-5 pb-5 pt-10">
                        <h3
                          className="font-heading font-semibold text-white leading-[1.15] tracking-tight"
                          style={{
                            fontSize: 'clamp(1.05rem, 1.5vw, 1.35rem)',
                            textShadow: '0 2px 14px rgba(14, 20, 38, 0.85), 0 1px 3px rgba(14, 20, 38, 0.7)',
                          }}
                        >
                          {course.title}
                        </h3>
                      </div>
                    </div>

                    {/* Body */}
                    <div className="p-5 space-y-3">
                      {promise && (
                        <p className="font-serif italic text-[#1F2A4A]/80 text-sm leading-snug">
                          « {promise} »
                        </p>
                      )}

                      <div className="grid grid-cols-2 gap-3 pt-3 border-t border-[#1F2A4A]/8">
                        <div>
                          <p className="text-sm text-[#1F2A4A] flex items-center gap-1">
                            <Clock className="w-3 h-3 text-[#C9B896]" />
                            {course.duration_days} jour{course.duration_days > 1 ? 's' : ''}
                          </p>
                        </div>
                        <div>
                          {next ? (
                            <p className="text-sm text-[#1F2A4A]">
                              {format(parseISO(next.start_date), 'd MMM yy', { locale: fr })}
                            </p>
                          ) : (
                            <p className="text-xs text-[#4A5568]/55 italic">À venir</p>
                          )}
                        </div>
                      </div>

                      {/* Seats progress */}
                      {next && (
                        <div className="space-y-1.5">
                          <div className="flex items-center justify-between text-[10px] text-[#4A5568]">
                            <span className="flex items-center gap-1"><Users className="w-2.5 h-2.5 text-[#C9B896]" /> {next.enrolled_count}/{next.available_spots}</span>
                            {spots > 0 && <span className="text-[#C9B896]">{spots} dispo</span>}
                          </div>
                          <div className="h-1 rounded-full bg-[#1F2A4A]/8 overflow-hidden">
                            <div
                              className={`h-full transition-all duration-700 ${
                                spots === 0 ? 'bg-red-500' : fillingFast ? 'bg-amber-500' : 'bg-emerald-600'
                              }`}
                              style={{ width: `${ratio}%` }}
                            />
                          </div>
                        </div>
                      )}

                      <div className="flex items-center justify-between pt-2">
                        <span className="text-[11px] text-[#4A5568] uppercase tracking-wider">Voir la formation</span>
                        <ArrowUpRight className="w-4 h-4 text-[#C9B896] group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-500" />
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          ) : (
            <ul className="border-t border-[#1F2A4A]/12" data-testid="onsite-formations-list">
              {filteredCourses.map((course, idx) => {
                const next = getNextSession(course.id);
                const spots = next ? next.available_spots - next.enrolled_count : 0;

                return (
                  <li
                    key={course.id}
                    className="group border-b border-dashed border-[#1F2A4A]/15 hover:bg-[#F0EBE0] transition-colors duration-500"
                    data-testid={`onsite-course-card-${course.id}`}
                  >
                    <Link to={withLangPrefix(`/formation/${course.id}`, lang)} className="grid grid-cols-12 items-center gap-4 px-2 md:px-4 py-7 md:py-9">
                      <div className="col-span-2 md:col-span-1 text-xs font-mono text-[#C9B896] tracking-wider">
                        {String(idx + 1).padStart(2, '0')}
                      </div>
                      <div className="col-span-10 md:col-span-5">
                        <p className="text-[11px] uppercase tracking-[0.25em] text-[#4A5568] mb-2">
                          {categoryLong[course.category] || course.category}
                        </p>
                        <h3 className="text-xl md:text-2xl font-light text-[#1F2A4A] group-hover:text-[#C9B896] transition-colors duration-300 leading-tight">
                          {course.title}
                        </h3>
                        <p className="text-sm text-[#4A5568]/85 mt-2 line-clamp-1 font-light hidden md:block">
                          {course.description}
                        </p>
                      </div>
                      <div className="hidden md:block col-span-2 text-sm text-[#4A5568] font-light">
                        <span className="block text-[10px] uppercase tracking-wider text-[#C9B896]/80 mb-0.5">Durée</span>
                        {course.duration_days} jour{course.duration_days > 1 ? 's' : ''}
                      </div>
                      <div className="hidden md:block col-span-2 text-sm text-[#4A5568] font-light">
                        {next ? (
                          <>
                            <span className="block text-[10px] uppercase tracking-wider text-[#C9B896]/80 mb-0.5">Prochaine</span>
                            {format(parseISO(next.start_date), 'd MMM yyyy', { locale: fr })}
                            {spots > 0 ? (
                              <span className="text-[10px] text-[#4A5568]/65 block">{spots} places</span>
                            ) : (
                              <span className="text-[10px] text-red-600/85 block">Complet</span>
                            )}
                          </>
                        ) : (
                          <span className="text-[#4A5568]/55 text-xs italic">À venir</span>
                        )}
                      </div>
                      <div className="col-span-12 md:col-span-2 flex items-baseline justify-end gap-4 md:gap-6 mt-3 md:mt-0">
                        <span className="font-light text-xl md:text-2xl text-[#1F2A4A]">
                          {course.price.toFixed(0)}<span className="text-sm text-[#C9B896] ml-1">€</span>
                        </span>
                        <span className="text-[#C9B896] text-xl group-hover:translate-x-2 transition-transform duration-500">→</span>
                      </div>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </section>

      {/* Modules à la carte */}
      {moduleCourses.length > 0 && (
        <section className="py-14 md:py-20 bg-[#F4EFE3]/60" data-testid="modules-carte-catalogue">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#C9B896] mb-2 font-semibold">À la carte</p>
            <h2 className="font-light text-3xl md:text-4xl leading-tight text-[#1F2A4A] mb-3">
              Modules <span className="font-serif italic">à la carte.</span>
            </h2>
            <p className="text-[#4A5568] text-sm font-light mb-10 max-w-2xl">
              Une compétence précise à acquérir&nbsp;? Chaque module se suit indépendamment — 1 500&nbsp;€ TTC, 2 à 3 jours en atelier à Istres.
            </p>
            {['sellerie', 'recovering'].map((parent) => {
              const mods = moduleCourses.filter(m => m.module_parent === parent);
              if (!mods.length) return null;
              return (
                <div key={parent} className="mb-10">
                  <h3 className="text-[#1F2A4A] font-medium text-lg mb-4 flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#C9B896]" />
                    {parent === 'sellerie' ? 'Modules Sellerie-Garnissage' : 'Modules Recovering & Covering'}
                  </h3>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {mods.map((m) => (
                      <Link
                        key={m.id}
                        to={withLangPrefix(`/formation/${m.id}`, lang)}
                        className="group bg-white rounded-xl border border-[#1F2A4A]/10 p-5 flex flex-col hover:border-[#C9B896]/60 hover:shadow-md transition-all"
                        data-testid={`module-catalogue-card-${m.id}`}
                      >
                        <p className="text-[11px] uppercase tracking-[0.15em] text-[#8a7340] font-bold mb-1.5">{m.duree_detail || `${m.duration_days} jours`}</p>
                        <h4 className="text-[#1F2A4A] font-medium text-sm leading-snug mb-2 group-hover:text-[#C9B896] transition-colors">{m.title}</h4>
                        <p className="text-xs text-[#4A5568] leading-relaxed flex-1 line-clamp-3">{m.description}</p>
                        <p className="mt-3 pt-2 border-t border-dashed border-[#1F2A4A]/10 text-[#1F2A4A] font-bold text-xl">{m.price?.toFixed(0)}<span className="text-sm text-[#8a7340] font-semibold"> € TTC</span></p>
                      </Link>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Qualiopi footer cta */}
      <section className="pb-24 md:pb-32">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <a
            href="/qualiopi/certificat-qualiopi-sellerie-academie.pdf"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-5 px-7 py-4 rounded-full border border-[#C9B896]/40 mb-8 bg-white hover:bg-[#FAF7F0] hover:shadow-md transition-all"
            data-testid="onsite-qualiopi-link"
          >
            <QualiopiBadge size="md" />
            <div className="text-left border-l border-[#1F2A4A]/10 pl-5">
              <p className="text-xs font-semibold text-[#1F2A4A] uppercase tracking-[0.2em]">Certifié Qualiopi</p>
              <p className="text-[11px] text-[#4A5568]">N° QUA23050043 · Valide jusqu'au 10/07/2026</p>
              <p className="text-[10px] text-[#C9B896] uppercase tracking-wider mt-0.5">Plusieurs solutions de financement disponibles</p>
            </div>
          </a>
          <p className="text-[#4A5568] text-base font-light max-w-xl mx-auto leading-relaxed mb-8">
            Hésitant sur la formation la plus adaptée à ton projet&nbsp;? Échange directement avec Benki avant de t'inscrire.
          </p>
          <Link to={withLangPrefix('/contact', lang)}>
            <Button className="bg-[#1F2A4A] text-[#FAF7F0] hover:bg-[#324166] rounded-full px-9 py-7 text-sm uppercase tracking-[0.25em] font-medium shadow-xl shadow-[#1F2A4A]/15" data-testid="onsite-contact-cta">
              Demander conseil
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};
